// Lightweight global WebSocket client (singleton) for the web app
// - Connects to same-origin /api/ws (ws or wss based on page protocol)
// - Authenticates immediately using JWT from cookie or localStorage
// - Auto-reconnects with backoff, re-auths on reconnect
// - Simple pub/sub API: on(type, handler) -> unsubscribe()

const WS_PATH = '/api/ws';

// Debug logging controlled by REACT_APP_WS_DEBUG env variable
const WS_DEBUG = process.env.REACT_APP_WS_DEBUG === 'true';

function wsLog(...args) {
  if (WS_DEBUG) {
    console.log('[WS]', ...args);
  }
}

function wsWarn(...args) {
  if (WS_DEBUG) {
    console.warn('[WS]', ...args);
  }
}

function wsError(...args) {
  // Always log errors, even when debug is off
  console.error('[WS]', ...args);
}

function getCookieValue(name) {
  const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
  return match ? match[2] : null;
}

function getJwtToken() {
  // Prefer cookie (used elsewhere in the app), fallback to localStorage
  return getCookieValue('jwtToken') || window.localStorage.getItem('jwtToken');
}

class WSClient {
  constructor() {
    this.ws = null;
    this.status = 'idle'; // idle | connecting | open | closed
    this.reconnectAttempts = 0;
    this.maxBackoff = 15000; // 15s cap
    this.listeners = new Map(); // type -> Set<fn>
    this.genericListeners = new Set(); // all messages
    this._connectRequested = false;
    this._pingTimer = null;
    wsLog('WSClient initialized. Debug logging:', WS_DEBUG ? 'ENABLED' : 'DISABLED');
  }

  _url() {
    // Allow dev override via env var (e.g. ws://localhost:3003/api/ws)
    const devWsUrl = process.env.REACT_APP_WS_URL;
    if (devWsUrl) {
      wsLog('WebSocket URL (dev override):', devWsUrl);
      return devWsUrl;
    }
    const scheme = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    // In CRA dev (port 3000), the WS server runs on a separate port (3003)
    // In production, same-origin is used
    let host = window.location.host;
    if (window.location.port === '3000') {
      host = 'localhost:3003';
    }
    const url = scheme + '//' + host + WS_PATH;
    wsLog('WebSocket URL:', url);
    return url;
  }

  _emit(type, payload) {
    const typeListenerCount = this.listeners.has(type) ? this.listeners.get(type).size : 0;
    const genericListenerCount = this.genericListeners.size;
    
    wsLog(`Emitting event type="${type}"`, {
      payload,
      typeListeners: typeListenerCount,
      genericListeners: genericListenerCount,
      totalListeners: typeListenerCount + genericListenerCount
    });

    if (this.listeners.has(type)) {
      for (const fn of this.listeners.get(type)) {
        try { 
          wsLog(`Calling type-specific listener for "${type}"`);
          fn(payload); 
        } catch (e) { 
          wsError('ws listener error for type', type, e); 
        }
      }
    } else {
      wsLog(`No type-specific listeners registered for "${type}"`);
    }
    
    for (const fn of this.genericListeners) {
      try { 
        wsLog('Calling generic listener');
        fn(payload); 
      } catch (e) { 
        wsError('ws generic listener error', e); 
      }
    }
  }

  on(type, handler) {
    if (!type || typeof handler !== 'function') return () => {};
    if (!this.listeners.has(type)) this.listeners.set(type, new Set());
    this.listeners.get(type).add(handler);
    wsLog(`Registered listener for event type "${type}". Total listeners for this type: ${this.listeners.get(type).size}`);
    return () => {
      const set = this.listeners.get(type);
      if (set) {
        set.delete(handler);
        wsLog(`Unregistered listener for event type "${type}". Remaining: ${set.size}`);
        if (set.size === 0) this.listeners.delete(type);
      }
    };
  }

  onAny(handler) {
    if (typeof handler !== 'function') return () => {};
    this.genericListeners.add(handler);
    wsLog(`Registered generic listener. Total generic listeners: ${this.genericListeners.size}`);
    return () => {
      this.genericListeners.delete(handler);
      wsLog(`Unregistered generic listener. Remaining: ${this.genericListeners.size}`);
    };
  }

  connect() {
    this._connectRequested = true;
    wsLog('Connect requested. Current status:', this.status);
    if (this.ws && (this.status === 'connecting' || this.status === 'open')) {
      wsLog('Already connecting/connected, skipping');
      return;
    }
    this._open();
  }

  disconnect() {
    this._connectRequested = false;
    wsLog('Disconnect requested');
    this._stopPing();
    if (this.ws) {
      try { this.ws.close(); } catch (_) {}
    }
    this.ws = null;
    this.status = 'closed';
  }

  _stopPing() {
    if (this._pingTimer) {
      clearInterval(this._pingTimer);
      this._pingTimer = null;
    }
  }

  send(obj) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      try {
        wsLog('Sending message:', obj);
        this.ws.send(JSON.stringify(obj));
        return true;
      } catch (e) {
        wsError('ws send error', e);
      }
    } else {
      wsWarn('Cannot send - WebSocket not open. State:', this.ws?.readyState, 'Message:', obj);
    }
    return false;
  }

  _open() {
    this.status = 'connecting';
    const url = this._url();
    wsLog('Opening WebSocket connection to:', url);
    let ws;
    try {
      ws = new WebSocket(url);
    } catch (e) {
      wsError('ws init error', e);
      return this._scheduleReconnect();
    }
    this.ws = ws;

    ws.addEventListener('open', () => {
      this.status = 'open';
      this.reconnectAttempts = 0;
      wsLog('WebSocket OPENED successfully');
      // Authenticate immediately
      const token = getJwtToken();
      if (token) {
        wsLog('JWT token found, sending auth message');
        this.send({ token });
      } else {
        wsWarn('No JWT token found immediately after open, will retry in 1s');
        // Try to auth shortly after open in case login just set cookie
        setTimeout(() => {
          const lateToken = getJwtToken();
          if (lateToken) {
            wsLog('JWT token found on retry, sending auth message');
            this.send({ token: lateToken });
          } else {
            wsWarn('Still no JWT token after 1s delay');
          }
        }, 1000);
      }
      // Start keepalive ping every 25s to prevent nginx/proxy idle timeout
      this._stopPing();
      this._pingTimer = setInterval(() => {
        if (this.ws && this.ws.readyState === WebSocket.OPEN) {
          this.send({ type: 'ping' });
        }
      }, 25000);
    });

    ws.addEventListener('message', (evt) => {
      try {
        const msg = JSON.parse(evt.data);
        wsLog('◀ RECEIVED WebSocket message:', {
          type: msg.type,
          timestamp: msg.timestamp,
          data: msg.data,
          message: msg.message,
          fullMessage: msg
        });
        // Route by type if present; always notify generic listeners
        if (msg && msg.type) this._emit(msg.type, msg);
        this._emit('*', msg);
      } catch (e) {
        wsWarn('ws parse error', e, 'Raw data:', evt.data);
      }
    });

    ws.addEventListener('close', (evt) => {
      this.status = 'closed';
      this.ws = null;
      this._stopPing();
      wsLog('WebSocket CLOSED', {
        code: evt.code,
        reason: evt.reason,
        wasClean: evt.wasClean,
        reconnectRequested: this._connectRequested
      });
      if (this._connectRequested) this._scheduleReconnect();
    });

    ws.addEventListener('error', (err) => {
      wsError('WebSocket ERROR event:', err);
      // Let the close handler drive reconnect
    });
  }

  _scheduleReconnect() {
    if (!this._connectRequested) return;
    this.reconnectAttempts += 1;
    const backoff = Math.min(this.maxBackoff, 500 * Math.pow(2, this.reconnectAttempts));
    wsLog(`Scheduling reconnect attempt #${this.reconnectAttempts} in ${backoff}ms`);
    setTimeout(() => {
      if (this._connectRequested) {
        wsLog(`Reconnect attempt #${this.reconnectAttempts} starting now`);
        this._open();
      }
    }, backoff);
  }
}

const wsClient = new WSClient();
export default wsClient;